import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let the browser set multipart boundary for file uploads
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const reqUrl = String(error.config?.url || '');
      const isPaymentVerify = /\/payments\/bookings\/[^/]+\/verify/i.test(reqUrl);
      // Keep session during Razorpay verify retry; signing out loses the pending payment flow.
      if (!reqUrl.includes('/content/brochure-download') && !isPaymentVerify) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  sendOtp: (phone) => api.post('/auth/send-otp', { phone: phone.replace(/\D/g, '').slice(-10) }),
  verifyOtp: (phone, otp, payload = {}) =>
    api.post('/auth/verify-otp', { phone: phone.replace(/\D/g, '').slice(-10), otp, ...payload })
};

export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  getDashboard: () => api.get('/users/dashboard'),
  updateProfile: (data) => api.put('/users/profile', data),
  uploadProfileImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/uploads/images', formData);
  }
};

export const contentAPI = {
  // Hero Sliders
  getHeroSliders: () => api.get('/content/hero-sliders'),
  createHeroSlider: (data) => api.post('/content/hero-sliders', data),
  updateHeroSlider: (id, data) => api.put(`/content/hero-sliders/${id}`, data),
  deleteHeroSlider: (id) => api.delete(`/content/hero-sliders/${id}`),
  
  // Events
  getEvents: (params) => api.get('/content/events', { params }),
  getEventBySlug: (slug) => api.get(`/content/events/${slug}`),
  createEvent: (data) => api.post('/content/events', data),
  updateEvent: (id, data) => api.put(`/content/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/content/events/${id}`),
  
  // Highlighted Events (homepage)
  getHighlightedEvents: () => api.get('/content/highlighted-events'),
  
  // Blogs
  getBlogs: (params) => api.get('/content/blogs', { params }),
  getBlogBySlug: (slug) => api.get(`/content/blogs/slug/${encodeURIComponent(slug)}`),
  createBlog: (data) => api.post('/content/blogs', data),
  
  // Testimonials
  getTestimonials: () => api.get('/content/testimonials'),
  createTestimonial: (data) => api.post('/content/testimonials', data),
  
  // Partners
  getPartners: () => api.get('/content/partners'),
  createPartner: (data) => api.post('/content/partners', data),
  
  // Stats
  getStats: () => api.get('/content/stats'),
  updateStats: (stats) => api.put('/content/stats', { stats }),
  
  // Login Page Images
  getLoginPageImages: () => api.get('/content/login-page-images'),
  createLoginPageImage: (data) => api.post('/content/login-page-images', data),
  updateLoginPageImage: (id, data) => api.put(`/content/login-page-images/${id}`, data),
  deleteLoginPageImage: (id) => api.delete(`/content/login-page-images/${id}`),

  // Site Settings (section labels for homepage etc.)
  getSiteSettings: () => api.get('/content/site-settings'),

  // Expedition page (title, subtitle, treks)
  getExpeditionSection: () => api.get('/content/expedition-section'),

  // Callback request (public)
  submitCallbackRequest: (data) => api.post('/content/callback-request', data),

  // Volunteer inquiry from About CTA popup (public)
  submitVolunteerInquiry: (data) => api.post('/content/volunteer-inquiry', data),

  // PDF request from event card "Get PDF" popup (public)
  submitPdfRequest: (data) => api.post('/content/pdf-request', data),

  /** Public: brochure/PDF on private app S3 — streams file (blob), same idea as admin ticket download */
  downloadBrochureAsset: (absoluteUrl, { name } = {}) =>
    api.get('/content/brochure-download', {
      params: {
        url: absoluteUrl,
        ...(name ? { name: String(name).trim() } : {})
      },
      responseType: 'blob'
    }),

  // Home categories (public)
  getHomeCategories: (params) => api.get('/home-categories', { params })
};

export const dynamicAPI = {
  getCategories: (params) => api.get('/categories', { params }),
  getTreks: (params) => api.get('/treks', { params }),
  getTrekById: (id) => api.get(`/treks/${id}`),
  getEventBatches: (params) => api.get('/event-batches', { params }),
  getUpcomingTreks: (params) => api.get('/event-batches/upcoming/treks', { params }),
  /** Public: count "More details" click for popularity sort */
  recordTrekDetailClick: (trekId) =>
    api.post('/event-batches/upcoming/treks/detail-click', { trekId: Number(trekId) }),
  getBatchesByTrekSlug: (slug) => api.get(`/event-batches/upcoming/for-trek/${encodeURIComponent(slug)}`),
  getEventBatchById: (id) => api.get(`/event-batches/${id}`),
  getSpecialEvents: (params) => api.get('/special-events', { params }),
  getSpecialEventBySlug: (slug) =>
    api.get(`/special-events/public/${encodeURIComponent(slug)}`),
  getTreksBySlug: (slug) => api.get(`/${slug}/trek`),
  /** Public attraction detail (slug or attraction UUID). No auth. */
  getAttractionPublic: (identifier) =>
    api.get(`/attractions/public/${encodeURIComponent(identifier)}`)
};

export const bookingAPI = {
  create: (payload) => api.post('/bookings', payload),
  checkDuplicate: (params) => api.get('/bookings/check-duplicate', { params }),
  getById: (id) => api.get(`/bookings/${id}`),
  getMyBookings: () => api.get('/bookings/my'),
  downloadTicketFile: (bookingId, params = {}) =>
    api.get(`/bookings/${bookingId}/ticket-download`, {
      params,
      responseType: 'blob'
    }),
  /**
   * User dashboard: request cancellation for own booking (bookingId = booking_extensions.id).
   * Pass a FormData with bookingId, title, details, and optional file field "attachment".
   */
  requestCancellation: (formData) =>
    api.post('/cancellations/request', formData, {
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }
      ]
    })
};

export const addOnsAPI = {
  getByTrekId: (trekId) => api.get('/add-ons', { params: trekId ? { trekId } : {} })
};

export const paymentAPI = {
  getConfig: () => api.get('/payments/config'),
  createBookingOrder: (bookingId, paymentFor = 'advance') =>
    api.post(`/payments/bookings/${bookingId}/create-order`, { paymentFor }),
  getBookingPaymentStatus: (bookingId) => api.get(`/payments/bookings/${bookingId}/status`),
  verifyBookingPayment: (bookingId, payload) =>
    api.post(`/payments/bookings/${bookingId}/verify`, payload)
};

export const contactAPI = {
  getOffices: () => api.get('/contact/offices'),
  submitInquiry: (payload) => api.post('/contact/inquiries', payload)
};

export const aboutAPI = {
  getAbout: () => api.get('/about'),
  getProjectById: (id) => api.get(`/about/projects/${id}`)
};

export const teamAPI = {
  getTeam: () => api.get('/team')
};

export const careersAPI = {
  getCareers: () => api.get('/careers'),
  getJobById: (id) => api.get(`/careers/jobs/${id}`),
  uploadResume: (file) => {
    const formData = new FormData();
    formData.append('resume', file);
    return api.post('/uploads/resume', formData);
  },
  submitApplication: (jobId, data) => api.post(`/careers/jobs/${jobId}/apply`, data),
  getApplicationStatus: (applicationId) => api.get(`/careers/applications/${applicationId}/status`)
};

export default api;
